import { useEffect, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { Staff } from './types';

export type StaffLoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message?: string }
  | { phase: 'ready'; staff: Staff[] };

// The staff list hook — a small, unpaginated set. Mirrors the keep-alive
// contract of the other list hooks: a silent background revalidation on return
// to the page (activation tick) and on reload() after a same-page write, holding
// the current list across the fetch rather than flashing to empty.
export function useStaff(): { state: StaffLoadState; reload: () => void } {
  const [state, setState] = useState<StaffLoadState>({ phase: 'loading' });
  // The shared SWR trigger core (lib/useFetchTrigger) owns the reload nonce,
  // the activation+mutation tick, the fetch plan, and the generation guard.
  const trigger = useFetchTrigger();

  useEffect(() => {
    const run = trigger.begin();
    if (run.plan.showLoading) setState({ phase: 'loading' });
    api<Staff[]>('/staff', run.init)
      .then((body) => {
        if (!run.isCurrent()) return;
        run.markContent();
        setState({ phase: 'ready', staff: body.data ?? [] });
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return; // already redirected
        if (!run.isCurrent()) return;
        // A failed silent revalidation leaves the last-known list in place.
        if (run.isRevalidate && run.hasContent) return;
        setState({ phase: 'error', message: e instanceof Error ? e.message || undefined : undefined });
      });
    return run.cancel;
  }, [trigger.nonce, trigger.tick]);

  return { state, reload: trigger.reload };
}
